/**
 * 밝은 모던 오픈오피스 인테리어 (레퍼런스: 자연광 오픈오피스).
 * 오크 우드 플로어 + 좌측 유리 파티션 회의실(블랙 프레임/포스트잇) + 우측 대형 창(하늘/도시)
 * + 화이트 천장 매입등 + 배경 데스크 열/메쉬 체어/화분으로 구성한다.
 *
 * 성능 원칙: 배경 요소는 머티리얼별 지오메트리 병합(mergeGeometries)과 인스턴싱으로
 * 드로우콜을 억제하고, castShadow는 전부 끈다(그림자는 플레이 필드 전용).
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { noiseNormalMap, noiseRoughnessMap } from './textures';
import { makeCityWindowTexture, makeOakFloorTexture, makeWalnutTexture } from './officeTextures';
import { parkMiller } from './rng';
import { boxGeo, merged } from './geoUtils';

const ROOM = {
  // 물리 바닥 콜라이더 윗면(y=-1.0)에 맞춰 파편이 바닥에 정확히 안착하도록 한다.
  floorY: -1.0,
  // 천장을 살짝 낮춰 기본 카메라 각도에서 천장 라인+매입등이 프레임에 걸리게 한다.
  ceilY: 3.02,
  back: -7.5,
  // 카메라 뒤(안 보임)라 시각적으로는 무해하지만, 위로 세게 던진 오브젝트가 카메라 쪽(+z)으로
  // 날아갈 때 바닥이 여기서 끝나면 받아낼 바닥이 없어 영원히 낙하해 "사라진" 것처럼 보인다.
  // 카메라(z=3.3)보다 한참 뒤까지 넉넉히 확보해 어떤 강한 투척도 반드시 착지하게 한다.
  front: 10.5,
  left: -6.2,
  right: 6.2,
};

// 창 개구부(우측 벽 x=right) — 레퍼런스처럼 면적을 넓게.
const WIN = { z0: -6.6, z1: 2.6, y0: -0.65, y1: 2.72 };

// 회의실 유리 파티션(좌측~중앙 뒤) — 천장까지 닿는 풀하이트 글라스.
const GLASS_ROOM = { frontZ: -4.7, sideX: -1.5, topY: 2.98 };

// ---- 공유 머티리얼 ----
const OFFICE_MATS = {
  // 블랙 스틸(창 멀리언/유리 프레임/책상 다리 공용).
  steel: new THREE.MeshStandardMaterial({ color: 0x1b1d21, roughness: 0.45, metalness: 0.55 }),
  // 무광 블랙(배경 모니터/TV/포스터 프레임).
  blackMatte: new THREE.MeshStandardMaterial({ color: 0x111317, roughness: 0.6, metalness: 0.1 }),
  // 파티션/창 유리(투명 — transmission 대신 저비용 opacity).
  glass: new THREE.MeshPhysicalMaterial({
    color: 0xdcecf2,
    transparent: true,
    opacity: 0.14,
    roughness: 0.06,
    metalness: 0.0,
    side: THREE.DoubleSide,
    depthWrite: false,
  }),
  // 화이트 몰딩(창틀 하부 실 등).
  sill: new THREE.MeshStandardMaterial({ color: 0xeceae4, roughness: 0.8 }),
  // 블랙 메쉬 체어.
  chair: new THREE.MeshStandardMaterial({ color: 0x24262a, roughness: 0.78, metalness: 0.08 }),
  // 화분(그라파이트 포트)/줄기/잎.
  pot: new THREE.MeshStandardMaterial({ color: 0x35373a, roughness: 0.85 }),
  trunk: new THREE.MeshStandardMaterial({ color: 0x5c4630, roughness: 0.9 }),
  leaf: new THREE.MeshStandardMaterial({ color: 0x2f6b3a, roughness: 0.9 }),
};

export function buildOffice(scene: THREE.Scene): void {
  const group = new THREE.Group();

  buildFloor(group);
  buildWallsAndCeiling(group);
  buildWindowWall(group);
  buildGlassMeetingRoom(group);
  buildBackgroundDesks(group);
  buildChairs(group);
  buildPlants(group);

  scene.add(group);
}

// --- 바닥: 밝은 오크 플랭크 ---
function buildFloor(parent: THREE.Group): void {
  const oak = makeOakFloorTexture();
  // 타일 1장 ≈ 1.8m: 방 폭 12.4m 기준 repeat ≈ 7.
  oak.repeat.set(7, 7);
  const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(ROOM.right - ROOM.left, ROOM.front - ROOM.back),
    new THREE.MeshStandardMaterial({
      map: oak,
      roughness: 0.62,
      metalness: 0.0,
      roughnessMap: noiseRoughnessMap(256, 0.62, 0.18, 7),
    }),
  );
  floor.rotation.x = -Math.PI / 2;
  floor.position.set((ROOM.left + ROOM.right) / 2, ROOM.floorY, (ROOM.front + ROOM.back) / 2);
  floor.receiveShadow = true;
  parent.add(floor);
}

// --- 벽(화이트)/천장(화이트 플랫+매입등) ---
function buildWallsAndCeiling(parent: THREE.Group): void {
  // 살짝 그레이지 톤: 라이팅 후 회백색(0xf2f0ec 수준)으로 남아 블로우아웃을 막는다.
  const wallMat = new THREE.MeshStandardMaterial({
    color: 0xe9e6de,
    roughness: 0.95,
    normalMap: noiseNormalMap(256, 0.3, 6),
  });
  const wallH = ROOM.ceilY - ROOM.floorY;
  const wallCY = (ROOM.ceilY + ROOM.floorY) / 2;

  const back = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.right - ROOM.left, wallH), wallMat);
  back.position.set((ROOM.left + ROOM.right) / 2, wallCY, ROOM.back);
  back.receiveShadow = true;
  parent.add(back);

  const left = new THREE.Mesh(new THREE.PlaneGeometry(ROOM.front - ROOM.back, wallH), wallMat);
  left.rotation.y = Math.PI / 2;
  left.position.set(ROOM.left, wallCY, (ROOM.front + ROOM.back) / 2);
  parent.add(left);

  // 천장: 오프화이트 플랫(벽보다 살짝 밝게).
  const ceil = new THREE.Mesh(
    new THREE.PlaneGeometry(ROOM.right - ROOM.left, ROOM.front - ROOM.back),
    new THREE.MeshStandardMaterial({ color: 0xf1efe8, roughness: 0.94 }),
  );
  ceil.rotation.x = Math.PI / 2;
  ceil.position.set((ROOM.left + ROOM.right) / 2, ROOM.ceilY, (ROOM.front + ROOM.back) / 2);
  parent.add(ceil);

  // 사각 매입등 패널 그리드(발광 패널만 — 실제 라이트는 SceneManager의 key/hemi가 담당).
  // 뒤쪽(z가 작은) 행이 카메라 프레임 상단에 걸려 원근감을 만든다.
  const panelGeos: THREE.BufferGeometry[] = [];
  for (let z = -6.8; z <= 2.4; z += 1.8) {
    for (let x = -4.8; x <= 4.9; x += 1.6) {
      panelGeos.push(boxGeo(0.52, 0.03, 0.34, x, ROOM.ceilY - 0.015, z));
    }
  }
  const panels = merged(
    panelGeos,
    new THREE.MeshStandardMaterial({ color: 0xffffff, emissive: 0xfff8ec, emissiveIntensity: 2.4 }),
  );
  parent.add(panels);

  // 뒤벽 소품: 월넛 사이드보드 + 포스터(밋밋함 방지, 저비용).
  const sideboard = new THREE.Mesh(boxGeo(2.2, 0.7, 0.4, 1.6, ROOM.floorY + 0.35, ROOM.back + 0.22), deskTopMat());
  parent.add(sideboard);
  const posterFrame = new THREE.Mesh(boxGeo(1.15, 0.8, 0.03, 1.6, 1.35, ROOM.back + 0.03), OFFICE_MATS.blackMatte);
  parent.add(posterFrame);
  const posterArt = new THREE.Mesh(
    new THREE.PlaneGeometry(1.05, 0.7),
    new THREE.MeshStandardMaterial({ color: 0xc9d4d0, roughness: 0.9 }),
  );
  posterArt.position.set(1.6, 1.35, ROOM.back + 0.05);
  parent.add(posterArt);
}

// 배경 데스크 상판 머티리얼(절차 월넛 — 플레이 책상과 동일 톤) — 캐시 공유.
let cachedTopMat: THREE.MeshStandardMaterial | null = null;
function deskTopMat(): THREE.MeshStandardMaterial {
  if (!cachedTopMat) {
    cachedTopMat = new THREE.MeshStandardMaterial({
      map: makeWalnutTexture(),
      roughness: 0.65,
      metalness: 0.0,
    });
  }
  return cachedTopMat;
}

// --- 우측: 대형 창(하늘+도시) + 블랙 멀리언 ---
function buildWindowWall(parent: THREE.Group): void {
  const x = ROOM.right;
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xe9e6de, roughness: 0.95 });

  // 개구부 주변 벽 세그먼트(아래/위/앞뒤 필러) 병합.
  const segs: THREE.BufferGeometry[] = [
    boxGeo(0.16, WIN.y0 - ROOM.floorY, ROOM.front - ROOM.back, x, (WIN.y0 + ROOM.floorY) / 2, (ROOM.front + ROOM.back) / 2),
    boxGeo(0.16, ROOM.ceilY - WIN.y1, ROOM.front - ROOM.back, x, (ROOM.ceilY + WIN.y1) / 2, (ROOM.front + ROOM.back) / 2),
    boxGeo(0.16, WIN.y1 - WIN.y0, WIN.z0 - ROOM.back, x, (WIN.y0 + WIN.y1) / 2, (ROOM.back + WIN.z0) / 2),
    boxGeo(0.16, WIN.y1 - WIN.y0, ROOM.front - WIN.z1, x, (WIN.y0 + WIN.y1) / 2, (WIN.z1 + ROOM.front) / 2),
  ];
  parent.add(merged(segs, wallMat));

  // 창밖 배경판(하늘+도시) — 언릿 + fog 미적용으로 항상 밝게.
  const sky = new THREE.Mesh(
    new THREE.PlaneGeometry(WIN.z1 - WIN.z0 + 0.4, WIN.y1 - WIN.y0 + 0.4),
    new THREE.MeshBasicMaterial({ map: makeCityWindowTexture(), fog: false }),
  );
  sky.rotation.y = -Math.PI / 2;
  sky.position.set(x + 0.35, (WIN.y0 + WIN.y1) / 2, (WIN.z0 + WIN.z1) / 2);
  parent.add(sky);

  // 유리 한 장(은은한 반사감).
  const pane = new THREE.Mesh(new THREE.PlaneGeometry(WIN.z1 - WIN.z0, WIN.y1 - WIN.y0), OFFICE_MATS.glass);
  pane.rotation.y = -Math.PI / 2;
  pane.position.set(x - 0.02, (WIN.y0 + WIN.y1) / 2, (WIN.z0 + WIN.z1) / 2);
  parent.add(pane);

  // 블랙 멀리언: 상/하 프레임 + 수직 바 + 중간 트랜섬.
  const bars: THREE.BufferGeometry[] = [
    boxGeo(0.1, 0.09, WIN.z1 - WIN.z0 + 0.1, x - 0.02, WIN.y1, (WIN.z0 + WIN.z1) / 2),
    boxGeo(0.1, 0.09, WIN.z1 - WIN.z0 + 0.1, x - 0.02, WIN.y0, (WIN.z0 + WIN.z1) / 2),
    boxGeo(0.1, 0.05, WIN.z1 - WIN.z0 + 0.1, x - 0.02, 0.35, (WIN.z0 + WIN.z1) / 2),
  ];
  const nBars = 6;
  for (let i = 0; i <= nBars; i++) {
    const z = WIN.z0 + ((WIN.z1 - WIN.z0) / nBars) * i;
    bars.push(boxGeo(0.1, WIN.y1 - WIN.y0, 0.07, x - 0.02, (WIN.y0 + WIN.y1) / 2, z));
  }
  parent.add(merged(bars, OFFICE_MATS.steel));

  // 화이트 실(선반) + 소형 화분은 buildPlants에서 함께 병합.
  const sillBoard = new THREE.Mesh(boxGeo(0.3, 0.05, WIN.z1 - WIN.z0 + 0.2, x - 0.12, WIN.y0 - 0.02, (WIN.z0 + WIN.z1) / 2), OFFICE_MATS.sill);
  parent.add(sillBoard);
}

// --- 좌측~중앙 뒤: 유리 파티션 회의실 ---
function buildGlassMeetingRoom(parent: THREE.Group): void {
  const { frontZ, sideX, topY } = GLASS_ROOM;
  const h = topY - ROOM.floorY;
  const cy = (topY + ROOM.floorY) / 2;

  // 유리 2면(정면/측면)을 1개 메시로 병합.
  const frontGlass = new THREE.PlaneGeometry(sideX - ROOM.left, h);
  frontGlass.translate((ROOM.left + sideX) / 2, cy, 0);
  frontGlass.translate(0, 0, frontZ);
  const sideGlass = new THREE.PlaneGeometry(frontZ - ROOM.back, h);
  sideGlass.rotateY(Math.PI / 2);
  sideGlass.translate(sideX, cy, (ROOM.back + frontZ) / 2);
  parent.add(merged([frontGlass, sideGlass], OFFICE_MATS.glass));

  // 블랙 알루미늄 프레임: 포스트 + 상부 빔 + 하부 레일.
  const frames: THREE.BufferGeometry[] = [
    boxGeo(sideX - ROOM.left + 0.08, 0.09, 0.09, (ROOM.left + sideX) / 2, topY, frontZ),
    boxGeo(sideX - ROOM.left + 0.08, 0.07, 0.09, (ROOM.left + sideX) / 2, ROOM.floorY + 0.035, frontZ),
    boxGeo(0.09, 0.09, frontZ - ROOM.back, sideX, topY, (ROOM.back + frontZ) / 2),
    boxGeo(0.09, 0.07, frontZ - ROOM.back, sideX, ROOM.floorY + 0.035, (ROOM.back + frontZ) / 2),
  ];
  for (const px of [ROOM.left, -5.03, -3.86, -2.68, sideX]) {
    frames.push(boxGeo(0.08, h, 0.08, px, cy, frontZ));
  }
  for (const pz of [ROOM.back + 0.04, -6.1, frontZ]) {
    frames.push(boxGeo(0.08, h, 0.08, sideX, cy, pz));
  }
  // 도어 핸들 바(x=-2.68~-1.5 구간이 도어).
  frames.push(boxGeo(0.03, 0.6, 0.03, -2.5, 0.55, frontZ + 0.06));
  parent.add(merged(frames, OFFICE_MATS.steel));

  // 유리벽 포스트잇(컬러 사각형 다수) — InstancedMesh 1콜.
  buildPostIts(parent);

  // 회의실 내부: 월넛 테이블 + 블랙 다리 + 벽걸이 TV 실루엣.
  const table = new THREE.Mesh(boxGeo(2.0, 0.07, 0.9, -4.0, -0.385, -6.1), deskTopMat());
  parent.add(table);
  const tableLegs: THREE.BufferGeometry[] = [];
  for (const [lx, lz] of [[-4.85, -6.45], [-3.15, -6.45], [-4.85, -5.75], [-3.15, -5.75]]) {
    tableLegs.push(boxGeo(0.06, 0.68, 0.06, lx, ROOM.floorY + 0.34, lz));
  }
  parent.add(merged(tableLegs, OFFICE_MATS.steel));
  // 벽걸이 TV: 테이블과의 관계가 읽히도록 낮게 걸고 얇은 월마운트로 벽에 밀착.
  const tv = new THREE.Mesh(
    mergeGeometries([
      boxGeo(1.5, 0.85, 0.05, -4.0, 1.05, ROOM.back + 0.06),
      boxGeo(0.5, 0.5, 0.04, -4.0, 1.05, ROOM.back + 0.02),
    ]),
    OFFICE_MATS.blackMatte,
  );
  parent.add(tv);
}

function buildPostIts(parent: THREE.Group): void {
  // 노랑/핑크/오렌지/그린 포스트잇을 격자+지터로 유리 앞면에 부착(발광 없음).
  const colors = [0xf6d64a, 0xf6d64a, 0xf59fb4, 0xf5a24b, 0xb5d96b];
  const rand = parkMiller(42, 16807);

  const slots: { x: number; y: number; rot: number; color: number }[] = [];
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 6; col++) {
      if (rand() < 0.28) continue; // 듬성듬성
      slots.push({
        x: -5.75 + col * 0.32 + (rand() - 0.5) * 0.06,
        y: 1.6 - row * 0.34 + (rand() - 0.5) * 0.05,
        rot: (rand() - 0.5) * 0.22,
        color: colors[Math.floor(rand() * colors.length)],
      });
    }
  }

  const inst = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(0.095, 0.095),
    new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.85 }),
    slots.length,
  );
  const color = new THREE.Color();
  fillInstances(inst, slots.length, (i, mat4, quat, eul, one) => {
    const s = slots[i];
    quat.setFromEuler(eul.set(0, 0, s.rot));
    mat4.compose(new THREE.Vector3(s.x, s.y, GLASS_ROOM.frontZ + 0.012), quat, one);
    inst.setColorAt(i, color.setHex(s.color));
  });
  parent.add(inst);
}

/**
 * InstancedMesh 트랜스폼 채우기 공통 보일러플레이트(포스트잇·체어 공유).
 * 임시 mat4/quat/eul/one을 소유해 compose 콜백에 넘기고, 각 인스턴스를 채운 뒤
 * instanceMatrix.needsUpdate를 세운다. 회전/위치/색 등 인스턴스별 로직만 콜백에 남는다.
 */
function fillInstances(
  inst: THREE.InstancedMesh,
  count: number,
  compose: (i: number, mat4: THREE.Matrix4, quat: THREE.Quaternion, eul: THREE.Euler, one: THREE.Vector3) => void,
): void {
  const mat4 = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const eul = new THREE.Euler();
  const one = new THREE.Vector3(1, 1, 1);
  for (let i = 0; i < count; i++) {
    compose(i, mat4, quat, eul, one);
    inst.setMatrixAt(i, mat4);
  }
  inst.instanceMatrix.needsUpdate = true;
}

// --- 배경 데스크 열(월넛 상판+블랙 다리) + 모니터 실루엣 ---
function buildBackgroundDesks(parent: THREE.Group): void {
  const topY = ROOM.floorY + 0.75; // 일반 오피스 데스크 높이 0.75m
  // 상판 2열 병합.
  const tops: THREE.BufferGeometry[] = [
    boxGeo(4.2, 0.05, 0.85, 2.6, topY - 0.025, -2.9),
    boxGeo(3.6, 0.05, 0.85, 3.4, topY - 0.025, -5.7),
  ];
  const topMesh = merged(tops, deskTopMat());
  topMesh.receiveShadow = true;
  parent.add(topMesh);

  // 블랙 스틸 다리 병합.
  const legH = topY - 0.05 - ROOM.floorY;
  const legs: THREE.BufferGeometry[] = [];
  const legY = ROOM.floorY + legH / 2;
  for (const [lx, lz] of [
    [0.65, -2.9], [4.55, -2.9], [2.6, -2.9], // 1열(벤치 공유 다리)
    [1.75, -5.7], [5.05, -5.7], // 2열
  ]) {
    legs.push(boxGeo(0.07, legH, 0.7, lx, legY, lz));
  }
  parent.add(merged(legs, OFFICE_MATS.steel));

  // 배경 모니터 실루엣(꺼진 패널 + 스탠드) 병합.
  const monitors: THREE.BufferGeometry[] = [];
  const addMonitor = (mx: number, mz: number, ry: number) => {
    monitors.push(boxGeo(0.24, 0.02, 0.17, mx, topY + 0.01, mz, ry)); // 받침
    monitors.push(boxGeo(0.045, 0.3, 0.04, mx, topY + 0.16, mz, ry)); // 지주
    monitors.push(boxGeo(0.62, 0.36, 0.035, mx, topY + 0.4, mz, ry)); // 패널
  };
  addMonitor(1.5, -3.15, 0.08);
  addMonitor(3.6, -3.18, -0.06);
  addMonitor(2.8, -5.92, 0.05);
  addMonitor(4.3, -5.95, -0.1);
  parent.add(merged(monitors, OFFICE_MATS.blackMatte));
}

// --- 블랙 메쉬 체어(1지오메트리 병합 → InstancedMesh) ---
function buildChairs(parent: THREE.Group): void {
  const parts: THREE.BufferGeometry[] = [];
  // 로컬 좌표: 바닥 y=0, 정면 +z.
  const base = new THREE.CylinderGeometry(0.27, 0.3, 0.035, 10);
  base.translate(0, 0.02, 0);
  parts.push(base);
  const post = new THREE.CylinderGeometry(0.028, 0.028, 0.36, 8);
  post.translate(0, 0.22, 0);
  parts.push(post);
  parts.push(boxGeo(0.47, 0.065, 0.45, 0, 0.43, 0));
  const backrest = new THREE.BoxGeometry(0.45, 0.6, 0.055);
  backrest.rotateX(-0.1);
  backrest.translate(0, 0.78, -0.235);
  parts.push(backrest);
  for (const ax of [-0.255, 0.255]) {
    parts.push(boxGeo(0.05, 0.04, 0.28, ax, 0.585, -0.03));
    parts.push(boxGeo(0.04, 0.14, 0.04, ax, 0.5, -0.12));
  }
  const chairGeo = mergeGeometries(parts);
  for (const g of parts) g.dispose();

  // 배치: 오픈 데스크 열 4개 + 회의실 4개(회의 테이블 둘레 밀도 — 레퍼런스). (x, z, yaw)
  const placements: [number, number, number][] = [
    [1.7, -2.25, Math.PI + 0.15],
    [3.7, -2.3, Math.PI - 0.1],
    [2.9, -5.15, Math.PI + 0.05],
    [4.5, -5.1, Math.PI - 0.2],
    [-4.9, -6.1, Math.PI / 2],
    [-3.1, -6.1, -Math.PI / 2],
    [-4.55, -5.45, Math.PI + 0.06],
    [-3.45, -5.45, Math.PI - 0.06],
  ];
  const inst = new THREE.InstancedMesh(chairGeo, OFFICE_MATS.chair, placements.length);
  fillInstances(inst, placements.length, (i, mat4, quat, eul, one) => {
    const [px, pz, yaw] = placements[i];
    quat.setFromEuler(eul.set(0, yaw, 0));
    mat4.compose(new THREE.Vector3(px, ROOM.floorY, pz), quat, one);
  });
  parent.add(inst);
}

// --- 화분 식물(플로어 3개 + 창가 실 소형 3개) — 포트/줄기/잎 3콜 ---
function buildPlants(parent: THREE.Group): void {
  const rand = parkMiller(7, 48271);

  const pots: THREE.BufferGeometry[] = [];
  const trunks: THREE.BufferGeometry[] = [];
  const leaves: THREE.BufferGeometry[] = [];

  // 대형 플로어 플랜트(포토스/관엽 느낌의 잎 클러스터).
  const floorPlants: [number, number][] = [
    [5.1, -1.2], // 창가
    [-2.2, -3.9], // 회의실 코너 앞
    [5.4, -6.3], // 뒤쪽 창 끝
  ];
  for (const [px, pz] of floorPlants) {
    const pot = new THREE.CylinderGeometry(0.24, 0.19, 0.4, 12);
    pot.translate(px, ROOM.floorY + 0.2, pz);
    pots.push(pot);
    const trunk = new THREE.CylinderGeometry(0.025, 0.04, 0.55, 6);
    trunk.translate(px, ROOM.floorY + 0.6, pz);
    trunks.push(trunk);
    for (let i = 0; i < 10; i++) {
      const leaf = new THREE.IcosahedronGeometry(0.13 + rand() * 0.09, 1);
      leaf.scale(1, 0.55 + rand() * 0.3, 1);
      const ang = rand() * Math.PI * 2;
      const r = rand() * 0.32;
      leaf.translate(px + Math.cos(ang) * r, ROOM.floorY + 0.85 + rand() * 0.5, pz + Math.sin(ang) * r);
      leaves.push(leaf);
    }
  }

  // 창가 실 위 소형 화분.
  for (const pz of [-5.4, -2.0, 1.0]) {
    const pot = new THREE.CylinderGeometry(0.07, 0.055, 0.11, 10);
    pot.translate(ROOM.right - 0.12, WIN.y0 + 0.06, pz);
    pots.push(pot);
    for (let i = 0; i < 3; i++) {
      const leaf = new THREE.IcosahedronGeometry(0.05 + rand() * 0.03, 1);
      leaf.scale(1, 0.7, 1);
      leaf.translate(ROOM.right - 0.12 + (rand() - 0.5) * 0.08, WIN.y0 + 0.17 + rand() * 0.05, pz + (rand() - 0.5) * 0.08);
      leaves.push(leaf);
    }
  }

  parent.add(merged(pots, OFFICE_MATS.pot));
  parent.add(merged(trunks, OFFICE_MATS.trunk));
  parent.add(merged(leaves, OFFICE_MATS.leaf));
}
